import jsonpath from 'jsonpath';
import { mapMutations } from 'vuex';
import vueCustomScrollbar from 'vue-custom-scrollbar';
import 'vue-custom-scrollbar/dist/vueScrollbar.css';

import LoadingComponent from '@/components/shared/loading/loading-component';
import ToolbarInsert from '@/components/shared/toolbars/toolbar-insert';
import { HIDE_MODAL,
    SET_CHANGED_DATA,
    SHOW_ALERT,
    SHOW_PROMPT } from '@/store/types/mutation-types';
import { getComponent } from '@/sitecore/sitecore-data-service';
import { processDataSources } from '@/sitecore/sitecore-data-interface';
import { getDynamicDataTransformValue, dynamicMapAtom } from '@/utils/flex-dynamic-data';
import { addEllipses } from '@/utils/string';
import { walkObject } from '@/utils/object';
import { SCREEN_NAMES } from '@/data/constants/screen';
import mapFilters from '@/utils/map-filters';
import listenOnRootMixin from '@/mixins/listen-on-root';
import RichHtmlEditor from '@/components/shared/modal/rich-html-editor';

export default {
    components: {
        LoadingComponent,
        ToolbarInsert,
        vueCustomScrollbar,
        RichHtmlEditor,
    },

    mixins: [listenOnRootMixin],

    data() {
        return {
            componentUpdateTrigger: '',
            dataObject: null,
            displayName: 'Dynamic Collection',
            paneIndex: 0,

            screen: SCREEN_NAMES.COMPONENT_EDITOR,

            scrollSettings: {
                suppressScrollX: true,
            },

            selectedAtomIndex: -1,
            selectedAtomPreview: '',
            selectedComponent: {},

            // UI state
            isPreviewExpanded: false,
            isLoading: false,

            // saved search data
            atomCollection: [],
            selectedComponentId: '',

            editorCode: '',
            editorScriptsInProgress: {}, // dictionary to hold working transform scripts
            transformMode: false,

            showChild: false,
            childData: {},
            childComponentId: '',
            childMapping: [],
            breadcrumbItems: [],
        };
    },

    computed: {

        toolbarId() {
            return `dynamic-mapping-nested-${ this._uid }`;
        },

        richTextEditorId() {
            return `rich-html-editor-${ this._uid }`;
        },

        // styles - BEGIN
        // available (percentage) widths for the preview component window
        previewWidth() {
            return { collapsed: 30, expanded: 50 };
        },

        barStyles() {
            let left = '100%';

            if (this.selectedComponentId) {
                left = this.isPreviewExpanded
                    ? `${ 100 - this.previewWidth.expanded }%`
                    : `${ 100 - this.previewWidth.collapsed }%`;
            }

            return { left };
        },

        barCaret() {
            return this.isPreviewExpanded ? 'caret-right' : 'caret-left';
        },

        previewPanelStyles() {
            let width = '0%';

            if (this.selectedComponentId) {
                width = this.isPreviewExpanded
                    ? `${ this.previewWidth.expanded }%`
                    : `${ this.previewWidth.collapsed }%`;
            }

            return { width };
        },

        // styles - END

        // dropdown options - BEGIN

        atomCollectionOptions() {
            return this.atomCollection.map((atom, index) => {
                return {
                    text: atom.displayName,
                    value: index,
                };
            });
        },

        selectedAtom: {
            get() {
                return this.atomCollection[this.selectedAtomIndex];
            },
            set(value) {
                this.atomCollection[this.selectedAtomIndex] = value;
            },
        },

        selectedAtomDisplayName() {
            return this.selectedAtom?.displayName;
        },

        truncatedDataObject() {
            const MAX_CHARS = 70;
            if (!this.dataObject) return {};

            if (typeof this.dataObject !== 'object') {
                return this.dataObject;
            }

            // using json parsing to deep copy as
            // spread operator does not deep copy inner items
            const dataObjectNew = JSON.parse(JSON.stringify(this.dataObject));
            walkObject(dataObjectNew, (parentObj, key, value) => {
                if (typeof value === 'string' && value.length > MAX_CHARS) {
                    parentObj[key] = addEllipses({
                        text: value,
                        maxChars: MAX_CHARS,
                        token: '[...]',
                    });
                }
            });

            return dataObjectNew;
        },

        atomCollectionTransforms() {
            // hack to update the transform collection
            // without creating a watcher for atomCollection
            // eslint-disable-next-line no-unused-expressions
            this.componentUpdateTrigger;

            if (!this.atomCollection) return [];
            return this.atomCollection.filter(i => i.transformScript);
        },

        autoCompleteWords() {
            // eslint-disable-next-line prefer-const
            let words = ['$data', 'JSONPath', 'JSONPath.query($data, "")[0]'];

            if (this.dataObject) {
                walkObject(this.dataObject, (parentObj, key) => {
                    if (!Array.isArray(parentObj) && words.indexOf(key) < 0) {
                        words.push(key);
                    }
                });
            }

            return words;
        },

        selectedComponentName() {
            return this.selectedComponent
                ? this.selectedComponent.editorName
                : '';
        },

        selectedAtomIsDynamicCollection() {
            if (!this.selectedAtom) return false;

            return this.selectedAtom.name.startsWith('collection-dynamic');
        },

        isChild() {
            return this.breadcrumbItems.length > 1;
        },
    },

    watch: {
        selectedComponentName() {
            const currentBreadcrumb = this.selectedComponentName
                ? this.selectedComponentName
                : 'current';

            if (this.breadcrumbItems.length < 1) {
                this.breadcrumbItems.push(currentBreadcrumb);
            } else {
                this.breadcrumbItems.splice(this.breadcrumbItems.length - 1, 1, currentBreadcrumb);
            }
        },
    },

    methods: {
        ...mapFilters(['titleCase']),

        ...mapMutations({
            hideModal: HIDE_MODAL,
            setChangedData: SET_CHANGED_DATA,
            showAlert: SHOW_ALERT,
            showPrompt: SHOW_PROMPT,
        }),

        onComponentSelect(menuItem) {
            if (this.showChild) return;

            this.selectedComponentId = menuItem.props.componentItemID;
            this.isLoading = true;

            // wait for the animations to finish
            window.setTimeout(() => this.loadComponent(true), 350);
        },

        onBarClick() {
            this.isPreviewExpanded = !this.isPreviewExpanded;
        },

        onAtomSelect(index) {
            if (!this.selectedComponent || !this.atomCollection.length || index < 0) return;

            this.clearHighlighted();
            this.selectedAtomIndex = index;

            const editorCode = this.editorScriptsInProgress[this.selectedAtom.path]
                || this.selectedAtom.transformScript
                || '';
            this.$refs.codeEditor.setValue(editorCode);

            // a small timeout ensures the green active button border gets applied without any lag
            // ... lag which is caused by the preview component re-rendering when highlightElement() is called
            window.setTimeout(this.highlightElement, 50);
        },

        onHide() {
            this.paneIndex = 0;
        },

        onClickAway() {
            // close the dropdowns
            this.$root.$emit('flex::dropdown::close');
        },

        async loadComponent(newComponent) {
            const response = await getComponent(this.selectedComponentId);

            if (response.status === 'error') {
                this.showAlert({ description: response.error });
                return;
            }

            const componentData = processDataSources([response.data])[0];

            this.selectedComponent = null;
            await this.$nextTick();
            this.selectedComponent = componentData;

            if (newComponent) {
                this.selectedAtomIndex = -1;
                this.atomCollection = [];

                if (!this.isChild) {
                    this.dataObject = null;
                }
                this.flexComponentProcessor(componentData);
            } else if (this.atomCollectionTransforms.length) {
                this.atomCollectionTransforms.forEach((atom) => {
                    dynamicMapAtom(this.dataObject, this.selectedComponent, atom);
                });
            }

            this.isLoading = false;
        },

        flexComponentProcessor(component, currentPath = '$') {
            this.grabElements(component, currentPath);

            if (component.elements && component.elements.length > 0) {
                component.elements.forEach((element) => {
                    const newPath = `${ currentPath }.elements[${ element.pointer - 1 }]`;
                    this.flexComponentProcessor(element, newPath);
                });
            }
        },

        grabElements(component, currentPath) {
            const isAtom = component.name.includes('a-');

            const displayName = component.customAttributes.displayName
                ? component.customAttributes.displayName.replace(/"/g, '')
                : this.formatAtomName(component.name);

            if (Array.isArray(component.dynamicProps)) {
                // likely for the UI, this should be a single map
                // but on the data end, could still be multiple entries in array
                component.dynamicProps.forEach((prop) => {
                    const compObject = {
                        id: component.id,
                        name: component.name,
                        displayName: isAtom
                            ? `${ displayName } - ${ this.formatAtomProp(prop) }`
                            : `${ displayName } - URL`,
                        path: isAtom
                            ? `${ currentPath }.${ prop }`
                            : `${ currentPath }.href`,
                        valuePath: '',
                    };

                    this.atomCollection.push(compObject);
                });
            } else if (component.elementType === 'collection-dynamic') {
                this.atomCollection.push({
                    id: component.id,
                    name: component.name,
                    displayName: `${ displayName } - map`,
                    path: `${ currentPath }.map`,
                    valuePath: '',
                });
            } else {
                const compObject = {
                    id: component.id,
                    name: component.name,
                    displayName: isAtom
                        ? displayName
                        : `${ displayName } - URL`,
                    path: isAtom
                        ? `${ currentPath }.${ component.dynamicProps }`
                        : `${ currentPath }.href`,
                    valuePath: '',
                };

                this.atomCollection.push(compObject);
            }
        },

        populatePath({ atom, transformScript }) {
            let dataValue = '';

            if (transformScript) {
                const response = getDynamicDataTransformValue(this.dataObject, atom.path, transformScript);
                if (response.error) {
                    this.showAlert({ description: response.message });
                    return false;
                }

                atom.transformScript = transformScript;
                dataValue = response?.value;
            }

            if (atom.innerCollection) {
                const mapperPath = `${ atom.path.substring(0, atom.path.lastIndexOf('.')) }.dynamicDataMapper`;
                const componentPath = `${ atom.path.substring(0, atom.path.lastIndexOf('.')) }.componentId`;
                jsonpath.value(this.selectedComponent, mapperPath, atom.innerCollection.dynamicDataMapper);
                jsonpath.value(this.selectedComponent, componentPath, atom.innerCollection.selectedPremadeComponentId);
            }

            jsonpath.value(this.selectedComponent, atom.path, dataValue);
            return true;
        },

        highlightElement() {
            const highlightPath = this.selectedAtom.path;
            const targetObject = `${ highlightPath.substring(0, highlightPath.lastIndexOf('.')) }.classes`;
            const newClasses = `${ jsonpath.query(this.selectedComponent, targetObject)[0] } is-selected-element`;

            jsonpath.value(this.selectedComponent, targetObject, newClasses);
            this.updatePreviewComponent();
        },

        clearHighlighted() {
            if (this.selectedAtomIndex < 0) return;

            const highlightPath = this.selectedAtom.path;
            const targetObject = `${ highlightPath.substring(0, highlightPath.lastIndexOf('.')) }.classes`;
            const newClasses = jsonpath.query(this.selectedComponent, targetObject)[0].replace('is-selected-element', '');

            jsonpath.value(this.selectedComponent, targetObject, newClasses);
        },

        formatAtomName(atomName) {
            const name = atomName.replace('a-', '');

            return this.titleCase(name);
        },

        formatAtomProp(propName) {
            let formatted = '';

            // @TODO - check to see if any other prop names need to be handled here
            switch (propName) {
                case 'src':
                    formatted = 'Source';
                    break;

                case 'alt':
                    formatted = 'Alt Text';
                    break;

                case 'href':
                    formatted = 'URL';
                    break;

                default:
                    formatted = propName;
            }

            return this.titleCase(formatted);
        },

        updatePreviewComponent() {
            this.componentUpdateTrigger = Date.now().toString();
        },

        onEditorCodeChange(code) {
            this.editorScriptsInProgress[this.selectedAtom.path] = code;
        },

        insertIntoEditorCode(text) {
            if (this.showChild) return;

            if (!this.editorScriptsInProgress[this.selectedAtom.path]
                && !this.selectedAtom.transformScript) {
                text = `return ${ text };`;
            }
            this.$refs.codeEditor.insertText(text);
        },

        applyTransform() {
            let success = true;
            Object.keys(this.editorScriptsInProgress).forEach((key) => {
                if (success) {
                    const atom = this.atomCollection.find(i => i.path === key);
                    success = this.populatePath({
                        atom,
                        transformScript: this.editorScriptsInProgress[key],
                    });
                }
            });
            if (success) {
                this.updatePreviewComponent();
                this.toggleTransformMode();
            }
        },

        toggleTransformMode() {
            this.transformMode = !this.transformMode;
            if (this.transformMode) {
                this.$refs.codeEditor.focusEnd();
            } else {
                this.editorScriptsInProgress = {};
            }
        },

        selectAtom(item) {
            const index = this.atomCollection.indexOf(item);
            if (index > -1) {
                this.onAtomSelect(index);
            }
            return index;
        },

        onMappingEdit(item) {
            if (!item) return;

            this.selectAtom(item);
            this.toggleTransformMode();
        },

        onRemoveMapping(item) {
            this.showPrompt({
                title: 'Confirm Deletion',
                description: `Are you sure you want to delete mapping for ${ item.displayName }?`,
                onContinue: () => this.deleteMapping(item),
            });
        },

        deleteMapping(item) {
            if (!item) return;

            const atomIndex = this.selectAtom(item);
            this.selectedAtom.transformScript = '';
            jsonpath.value(this.selectedComponent, this.selectedAtom.path, '');
            if (atomIndex === this.selectedAtomIndex) {
                this.$refs.codeEditor.setValue('');
            }
            this.updatePreviewComponent();
        },

        handlePreviewAtomClick(id) {
            if (this.showChild) return;
            if (this.paneIndex === 0 || !id) return;

            const index = this.atomCollection.findIndex(atom => atom.id === id);
            if (index > -1) {
                this.onAtomSelect(index);
            }
        },

        handlePreviewAtomDoubleClick() {
            if (this.showChild) return;

            if (!this.transformMode) {
                this.toggleTransformMode();
            }
        },

        clearAtomCollectionTransforms() {
            for (let i = 0; i < this.atomCollection.length; i++) {
                if (this.atomCollection[i]) {
                    // clear the scripts
                    this.atomCollection[i].transformScript = null;

                    // update preview
                    this.populatePath({ atom: this.atomCollection[i], transformScript: '' });
                }
            }
            this.updatePreviewComponent();
        },

        openNested() {
            const transformScript = this.editorScriptsInProgress[this.selectedAtom.path];
            const response = getDynamicDataTransformValue(this.dataObject, this.selectedAtom.path, transformScript);
            if (response.error) {
                this.showAlert({ description: response.message });
                return false;
            }
            this.childData = Array.isArray(response.value)
                ? response.value[0]
                : response.value;
            this.childComponentId = this.selectedAtom.innerCollection?.selectedPremadeComponentId;
            this.childMapping = this.selectedAtom.innerCollection?.dynamicDataMapper;
            this.showChild = true;
        },

        onChildUpdated(innerCollection) {
            this.showChild = false;
            this.selectedAtom.innerCollection = innerCollection;
            this.applyTransform();
        },

        onChildCancel() {
            this.showChild = false;
        },

        onBreadcrumbClick(index) {
            if (index < 0) return;
            if (index === this.breadcrumbItems.length - 1) return;

            this.$emit('breadcrumb', index);
        },

        onChildBreadcrumbClick(index) {
            this.showChild = false;
            if (this.breadcrumbItems.length - index > 1) {
                this.$emit('breadcrumb', index);
            }
        },
    },
};
